import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clock,
  Download,
  FileX,
  Loader2,
  Printer,
  RotateCcw,
  ShieldAlert,
  Undo2,
  XCircle,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SortableHeader, nextSort } from '@/components/ui/sortable-header'
import { PaginationControlsComponent } from '@/components/ui/pagination-controls'
import { usePagination } from '@/hooks/usePagination'
import apiClient, { ordersBrowserQueryParams } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { useFiscalStatus } from '@/hooks/useFiscalStatus'
import { usePraInvoiceEnabled } from '@/hooks/usePraInvoiceEnabled'
import { ENABLED_ORDER_TYPES_KEY, fetchEnabledOrderTypes, useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
  formatDateHuman,
  formatDateRangeHuman,
  formatDateTimeHuman,
  formatTimeHuman,
  parseIsoDate,
  toIsoDate,
  cn,
} from '@/lib/utils'
import { printPraTaxInvoice } from '@/lib/printPraTaxInvoice'
import {
  getCashierNameFromStorage,
  parseReceiptSettings,
  checkoutStoredIntentToReceiptPaymentMethod,
} from '@/lib/printCustomerReceipt'
import { printGuestReceiptForOrderId } from '@/lib/printGuestReceiptReplay'
import { printRefundReceipt } from '@/lib/printRefundReceipt'
import {
  displayLabelForOrderType,
  normalizeOrderTypeId,
  type OrderTypeConfig,
} from '@/lib/orderTypeSettings'
import { FiscalNoteDialog } from '@/components/admin/FiscalNoteDialog'
import { UndoStaffMealDialog } from '@/components/admin/UndoStaffMealDialog'
import { getStoredUserRole } from '@/lib/staff-roles'
import { orderPaymentLabel } from '@/lib/houseAccounts'
import { CreditNoteView } from '@/components/admin/CreditNoteView'
import type { OrdersBrowserParams, OrdersBrowserRow, OrdersBrowserSortKey } from '@/types'
import { DetailRow, OrderReceiptCard } from '@/components/admin/OrderReceiptCard'
import { OrdersFilterBar, type FiscalFilter, type PraFilter } from './OrdersFilterBar'
import { activeFilterCount, type OrdersBrowserFilters } from './ordersBrowserFilters'

/** Columns that read newest / largest first on the first click. */
const SORT_DEFAULT_DESC: readonly OrdersBrowserSortKey[] = ['time', 'date', 'total']

interface Props {
  /** Single business day (the Daily Sales drawer). Omit for a range. */
  day?: Date
  /** Range-wide list (Orders tab) — ISO dates, ≤ 93 days. Ignored when `day` is set. */
  from?: string
  to?: string
  /** F-RPT-N30 — facets + sort, owned by the caller (URL-backed). */
  filters: OrdersBrowserFilters
  onFiltersChange: (next: OrdersBrowserFilters) => void
  /** When set (invoice-search deep-link), auto-open this order's drawer once
   *  the list has loaded and contains it. The backend pins it to the top of
   *  page 1 and lets it through the empty-cancel filter. */
  initialOrderId?: string
}

export function OrdersBrowserTab({ day, from, to, filters, onFiltersChange, initialOrderId }: Props) {
  const { toast } = useToast()
  const { formatCurrency } = useCurrency()
  const queryClient = useQueryClient()
  // Gate PRA / FBR UI to the tenant's configured mode. PRA slip is governed by
  // app-setting pra_invoice_enabled; FBR (digital submission) by the fiscal
  // authority. Independent — a tenant can run one, both, or neither.
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()

  const [search, setSearch] = useState('')
  // Debounced so the list re-queries once per pause, not per keystroke.
  const [searchQ, setSearchQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 300)
    return () => clearTimeout(t)
  }, [search])
  const [praFilter, setPraFilter] = useState<PraFilter>('all')
  const [fiscalFilter, setFiscalFilter] = useState<FiscalFilter>('all')
  // Empty open-tab releases flood Daily Sales (Rs 0 Cancelled). Hide by default;
  // toggle on for floor-audit of who released which table.
  const [showEmptyCancels, setShowEmptyCancels] = useState(false)
  const [printingId, setPrintingId] = useState<string | null>(null)
  const [printingGuestId, setPrintingGuestId] = useState<string | null>(null)
  const [printingRefundId, setPrintingRefundId] = useState<string | null>(null)
  const [drawerOrderId, setDrawerOrderId] = useState<string | null>(null)
  const [noteDialog, setNoteDialog] = useState<{ order: OrdersBrowserRow } | null>(null)
  const [undoMealFor, setUndoMealFor] = useState<OrdersBrowserRow | null>(null)
  const [viewCreditNoteId, setViewCreditNoteId] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  const dayISO = day ? toIsoDate(day) : undefined
  const rangeMode = !dayISO
  const fromISO = dayISO ?? from ?? toIsoDate(new Date())
  const toISO = dayISO ?? to ?? fromISO

  // Sort: a range list defaults to newest first across days; a day list to
  // newest first within the day. The user's pick (URL) wins.
  const sortKey: OrdersBrowserSortKey = filters.sort ?? (rangeMode ? 'date' : 'time')
  const sortDir = filters.sort ? filters.dir : 'desc'
  const onSort = (key: OrdersBrowserSortKey) => {
    const next = nextSort({ key: filters.sort ?? sortKey, dir: sortDir }, key, SORT_DEFAULT_DESC)
    onFiltersChange({ ...filters, sort: next.key, dir: next.dir })
  }

  // Page resets whenever anything that changes the result set changes —
  // derived, not effect-driven, so there is no stale-page render.
  const pagination = usePagination({ initialPageSize: 50 })
  const resultKey = [
    fromISO, toISO, searchQ, filters.orderTypes.join(','), filters.payment, filters.subMethod,
    filters.status, praFilter, fiscalFilter, showEmptyCancels, sortKey, sortDir, pagination.pageSize,
  ].join('|')
  const pageRef = useRef({ key: resultKey, page: 1 })
  if (pageRef.current.key !== resultKey) {
    pageRef.current = { key: resultKey, page: 1 }
    if (pagination.page !== 1) pagination.goToPage(1)
  }
  const page = pageRef.current.key === resultKey ? pagination.page : 1

  const listParams: OrdersBrowserParams = {
    ...(dayISO ? { date: dayISO } : { from: fromISO, to: toISO }),
    search: searchQ || undefined,
    order_types: filters.orderTypes,
    payment_method: filters.payment,
    payment_sub_method: filters.subMethod,
    status: filters.status,
    pra_filter: praFilter,
    fiscal_filter: fiscalFilter,
    sort: sortKey,
    dir: sortDir,
    include_empty_cancels: showEmptyCancels,
    focus: initialOrderId,
  }

  const ordersQuery = useQuery({
    queryKey: ['reports-v2', 'orders-browser', listParams, page, pagination.pageSize],
    queryFn: () => apiClient.getOrdersBrowser({ ...listParams, page, per_page: pagination.pageSize }),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  })

  // Order-type config (built-ins + custom channels) so the Order Type column
  // and the filter chips resolve human labels — including "Foodpanda".
  const { raw: orderTypeRows } = useEnabledOrderTypes()

  const data = ordersQuery.data?.data
  const orders = useMemo(() => data?.orders ?? [], [data])
  const summary = data?.summary
  const total = data?.pagination?.total ?? summary?.count ?? 0
  const emptyCancelCount = showEmptyCancels ? 0 : (summary?.empty_cancels ?? 0)
  const windowDays = data?.pra_window_days ?? 7

  // Ids seen in rows but missing from settings (disabled / legacy types) still
  // get a chip, so history stays filterable. Accumulated across pages.
  const observedTypesRef = useRef<Set<string>>(new Set())
  for (const o of orders) {
    if (o.order_type) observedTypesRef.current.add(normalizeOrderTypeId(o.order_type))
  }

  // Invoice-search deep-link: auto-open the requested order's drawer once it
  // appears in the loaded list. Guarded so it fires once — re-closing stays
  // closed, and a stale/unknown id is simply ignored.
  const autoOpenedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!initialOrderId || autoOpenedRef.current === initialOrderId) return
    if (orders.some((o) => o.id === initialOrderId)) {
      autoOpenedRef.current = initialOrderId
      setDrawerOrderId(initialOrderId)
    }
  }, [initialOrderId, orders])

  const filterCount = activeFilterCount(filters) + (praFilter !== 'all' ? 1 : 0) + (fiscalFilter !== 'all' ? 1 : 0) + (searchQ ? 1 : 0)
  const netRevenue = (summary?.gross ?? 0) - (summary?.credits ?? 0)
  const netOrders = (summary?.count ?? 0) - (summary?.credits_count ?? 0)

  const exportCsv = async () => {
    setExporting(true)
    try {
      // Same facets + sort as the screen, no page window (backend caps rows).
      const { date: _d, from: _f, to: _t, ...rest } = ordersBrowserQueryParams(listParams)
      await apiClient.exportReportCsv('orders', fromISO, toISO, rest)
      toast({ title: 'CSV downloaded', description: `${total.toLocaleString('en-US')} orders` })
    } catch (error) {
      toast({
        title: 'Export failed',
        description: error instanceof Error ? error.message : String(error),
        variant: 'destructive',
      })
    } finally {
      setExporting(false)
    }
  }

  const printPra = useMutation({
    mutationFn: async (row: OrdersBrowserRow) => {
      // Always pull the full order (with items) before sending to the printer —
      // the OrdersBrowser row is intentionally lightweight. Routed through
      // queryClient so the OrderDrawer's in-card receipt view shares this
      // fetch (one network call per drawer-open + print, not two).
      const [orderRes, settingsRes, orderTypes] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ['order-detail', row.id],
          queryFn: () => apiClient.getOrder(row.id),
        }),
        queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        }),
        // F-FE-N167: a label lookup must never stop a reprint.
        queryClient
          .fetchQuery({ queryKey: ENABLED_ORDER_TYPES_KEY, queryFn: () => fetchEnabledOrderTypes() })
          .catch((): OrderTypeConfig[] => []),
      ])
      if (!orderRes.success || !orderRes.data) {
        throw new Error(orderRes.message || 'Failed to load order')
      }
      if (!settingsRes.success || !settingsRes.data) {
        throw new Error(settingsRes.message || 'Failed to load receipt settings')
      }

      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const order = orderRes.data
      const paidAt = order.completed_at ? new Date(order.completed_at) : new Date()
      const method = checkoutStoredIntentToReceiptPaymentMethod(order.checkout_payment_method)

      const { invoiceNumber, printed } = await printPraTaxInvoice(order, cfg, {
        cashierName: getCashierNameFromStorage(),
        paymentMethod: method,
        paidAt,
        orderTypeLabel: displayLabelForOrderType(order.order_type, orderTypes),
        formatAmount: formatCurrency,
      })

      if (!printed) {
        throw new Error('Print dialog was cancelled')
      }

      // Recording the print is best-effort; failures here will surface on the
      // next refresh as the order keeps its existing state.
      await apiClient.markPraInvoicePrinted(order.id, invoiceNumber || undefined)
    },
    onSuccess: () => {
      toast({ title: 'PRA invoice printed', description: 'Audit log updated.' })
      queryClient.invalidateQueries({ queryKey: ['reports-v2', 'orders-browser'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (error: unknown) => {
      const msg =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : String(error)
      toast({
        title: 'Could not print PRA invoice',
        description: msg,
        variant: 'destructive',
      })
    },
    onSettled: () => setPrintingId(null),
  })

  const runPrintGuestReceipt = async (row: OrdersBrowserRow) => {
    setPrintingGuestId(row.id)
    try {
      const r = await printGuestReceiptForOrderId(apiClient, queryClient, row.id, {
        formatAmount: formatCurrency,
        getCashierName: getCashierNameFromStorage,
        isDuplicateCopy: true,
      })
      if (!r.ok) {
        toast({
          title: 'Could not print guest receipt',
          description: r.reason,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Guest receipt',
          description: 'Sent to print dialog.',
        })
      }
    } finally {
      setPrintingGuestId(null)
    }
  }

  // One-click reprint of the customer-facing refund receipt straight from the
  // credit-note sub-row — no need to open the CreditNoteView modal first.
  // Fetches the full fiscal note (the row only carries its id) plus receipt
  // branding, then dispatches the same printer the modal uses.
  const runPrintRefundReceipt = async (creditNoteId: string, orderNumber?: string) => {
    setPrintingRefundId(creditNoteId)
    try {
      const [noteRes, settingsRes] = await Promise.all([
        apiClient.getFiscalNote(creditNoteId),
        queryClient.fetchQuery({
          queryKey: ['settings', 'all'],
          queryFn: () => apiClient.getAllSettings(),
        }),
      ])
      if (!noteRes.success || !noteRes.data) {
        throw new Error(noteRes.message || 'Failed to load refund')
      }
      if (!settingsRes.success || !settingsRes.data) {
        throw new Error(settingsRes.message || 'Failed to load receipt settings')
      }
      const cfg = parseReceiptSettings(settingsRes.data as Record<string, unknown>)
      const { printed } = printRefundReceipt(noteRes.data, cfg, { orderNumber })
      if (!printed) throw new Error('Print dialog was cancelled')
      toast({ title: 'Refund receipt', description: 'Sent to print dialog.' })
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      toast({
        title: 'Could not print refund receipt',
        description: msg,
        variant: 'destructive',
      })
    } finally {
      setPrintingRefundId(null)
    }
  }

  const focusedOrder = useMemo(
    () => orders.find((o) => o.id === drawerOrderId) ?? null,
    [orders, drawerOrderId],
  )

  const scopeLabel = day
    ? formatDateHuman(day)
    : formatDateRangeHuman(parseIsoDate(fromISO) ?? new Date(), parseIsoDate(toISO) ?? new Date())

  const th = (label: string, key: OrdersBrowserSortKey, extra = '', align: 'left' | 'right' = 'left') => (
    <th className={cn(align === 'right' ? 'text-right' : 'text-left', 'px-4 py-2', extra)}>
      <SortableHeader label={label} sortKey={key} activeKey={sortKey} dir={sortDir} onSort={onSort} align={align} />
    </th>
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <OrdersFilterBar
            filters={filters}
            onChange={onFiltersChange}
            orderTypeRows={orderTypeRows}
            observedOrderTypeIds={observedTypesRef.current}
            search={search}
            onSearchChange={setSearch}
            praEnabled={praEnabled}
            praFilter={praFilter}
            onPraFilterChange={setPraFilter}
            fiscalEnabled={fiscalEnabled}
            fiscalFilter={fiscalFilter}
            onFiscalFilterChange={setFiscalFilter}
            emptyCancelCount={emptyCancelCount}
            showEmptyCancels={showEmptyCancels}
            onToggleEmptyCancels={() => setShowEmptyCancels((v) => !v)}
            windowDays={windowDays}
            trailing={
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 text-xs"
                disabled={exporting || total === 0}
                onClick={() => void exportCsv()}
              >
                {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Export CSV
              </Button>
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base">
            Orders {rangeMode ? '' : 'on '}{scopeLabel}
            {filterCount > 0 ? ' · filtered' : ''} ({total.toLocaleString('en-US')}
            {emptyCancelCount > 0 ? ` · ${emptyCancelCount} empty cancels hidden` : ''})
          </CardTitle>
          <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-3 flex-wrap">
            <span>
              Gross: <span className="font-semibold text-foreground">{formatCurrency(summary?.gross ?? 0)}</span>
            </span>
            {(summary?.credits ?? 0) > 0 ? (
              <>
                <span className="text-amber-700 dark:text-amber-400">
                  Credits: <span className="font-semibold">−{formatCurrency(summary?.credits ?? 0)}</span>{' '}
                  <span className="text-[11px]">({summary?.credits_count ?? 0})</span>
                </span>
                <span>
                  Net (after credits):{' '}
                  <span className="font-semibold text-foreground">{formatCurrency(netRevenue)}</span>{' '}
                  <span className="text-[11px]">({netOrders} orders)</span>
                </span>
              </>
            ) : null}
            {praEnabled && (
              <span>
                PRA printed:{' '}
                <span className="font-semibold text-foreground">
                  {summary?.pra_printed ?? 0}/{summary?.count ?? 0}
                </span>
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {ordersQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : ordersQuery.isError ? (
            <div className="p-10 text-center text-sm text-destructive">
              Failed to load orders: {String((ordersQuery.error as Error)?.message ?? 'unknown')}
            </div>
          ) : orders.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No orders match these filters for {scopeLabel}.
            </div>
          ) : (
            <div className={cn('overflow-x-auto', ordersQuery.isFetching && 'opacity-70 transition-opacity')}>
              <table className="w-full min-w-[560px] md:min-w-[840px] lg:min-w-[1000px] text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {th('Order #', 'order_number')}
                    {rangeMode && th('Date', 'date')}
                    {th('Time', 'time')}
                    {th('Table', 'table', 'hidden md:table-cell')}
                    {th('Server', 'server', 'hidden lg:table-cell')}
                    <th className="hidden lg:table-cell text-right px-4 py-2.5">Party</th>
                    {th('Total', 'total', '', 'right')}
                    {th('Payment', 'payment', 'hidden md:table-cell')}
                    {th('Order Type', 'order_type', 'hidden lg:table-cell')}
                    {th('Status', 'status')}
                    {praEnabled && <th className="hidden md:table-cell text-left px-4 py-2.5">PRA</th>}
                    {fiscalEnabled && <th className="hidden md:table-cell text-left px-4 py-2.5">FBR</th>}
                    <th className="text-right px-4 py-2.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {orders.map((row) => (
                    <OrdersRow
                      key={row.id}
                      row={row}
                      showDate={rangeMode}
                      orderTypeRows={orderTypeRows}
                      printingPra={printingId === row.id}
                      printingGuest={printingGuestId === row.id}
                      formatCurrency={formatCurrency}
                      onView={() => setDrawerOrderId(row.id)}
                      onPrintPra={() => {
                        setPrintingId(row.id)
                        printPra.mutate(row)
                      }}
                      onPrintGuest={() => {
                        void runPrintGuestReceipt(row)
                      }}
                      onCreditNote={() => setNoteDialog({ order: row })}
                      onUndoStaffMeal={() => setUndoMealFor(row)}
                      onViewCreditNote={(id) => setViewCreditNoteId(id)}
                      printingRefund={
                        !!row.credit_note_id && printingRefundId === row.credit_note_id
                      }
                      onPrintRefundReceipt={(id) =>
                        void runPrintRefundReceipt(id, row.order_number)
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > pagination.pageSize && (
            <div className="border-t px-4 py-3">
              <PaginationControlsComponent
                pagination={pagination}
                total={total}
                pageSizeOptions={[50, 100, 200]}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <OrderDrawer
        currentOrder={focusedOrder}
        formatCurrency={formatCurrency}
        onClose={() => setDrawerOrderId(null)}
        onPrintPra={() => {
          if (!focusedOrder) return
          setPrintingId(focusedOrder.id)
          printPra.mutate(focusedOrder)
        }}
        onPrintGuest={() => {
          if (!focusedOrder) return
          void runPrintGuestReceipt(focusedOrder)
        }}
        printingPra={!!focusedOrder && printingId === focusedOrder.id}
        printingGuest={!!focusedOrder && printingGuestId === focusedOrder.id}
        onCreditNote={() => focusedOrder && setNoteDialog({ order: focusedOrder })}
        onUndoStaffMeal={() => focusedOrder && setUndoMealFor(focusedOrder)}
        onViewCreditNote={(id) => setViewCreditNoteId(id)}
      />

      {undoMealFor && (
        <UndoStaffMealDialog
          order={undoMealFor}
          formatCurrency={formatCurrency}
          onClose={() => setUndoMealFor(null)}
          onSuccess={(result) => {
            toast({
              title: 'Staff meal undone',
              description: `${result.order_number} · ${formatCurrency(result.amount)} back on ${result.customer_name}'s allowance`,
            })
            queryClient.invalidateQueries({ queryKey: ['reports-v2', 'orders-browser'] })
            queryClient.invalidateQueries({ queryKey: ['orders'] })
            queryClient.invalidateQueries({ queryKey: ['order-detail', result.order_id] })
            queryClient.invalidateQueries({ queryKey: ['house-accounts'] })
            queryClient.invalidateQueries({ queryKey: ['counter-house-accounts'] })
            setUndoMealFor(null)
          }}
        />
      )}

      {viewCreditNoteId && (
        <CreditNoteView
          noteId={viewCreditNoteId}
          orderNumber={focusedOrder?.order_number}
          onClose={() => setViewCreditNoteId(null)}
        />
      )}

      {noteDialog && (
        <FiscalNoteDialog
          order={noteDialog.order}
          noteType={'credit'}
          onClose={() => setNoteDialog(null)}
          onSuccess={(note) => {
            toast({
              title: fiscalEnabled ? 'Credit note issued' : 'Refund issued',
              description: note.fiscal_details?.irn
                ? `IRN ${note.fiscal_details.irn}`
                : `Status: ${note.status}`,
            })
            queryClient.invalidateQueries({ queryKey: ['reports-v2', 'orders-browser'] })
            setNoteDialog(null)
            setViewCreditNoteId(note.id)
          }}
        />
      )}
    </div>
  )
}

function OrdersRow({
  row,
  showDate,
  orderTypeRows,
  printingPra,
  printingGuest,
  formatCurrency,
  onView,
  onPrintPra,
  onPrintGuest,
  onCreditNote,
  onUndoStaffMeal,
  onViewCreditNote,
  printingRefund,
  onPrintRefundReceipt,
}: {
  row: OrdersBrowserRow
  /** Range-wide lists show the business date; a single-day list does not. */
  showDate: boolean
  orderTypeRows: OrderTypeConfig[]
  printingPra: boolean
  printingGuest: boolean
  formatCurrency: (n: number) => string
  onView: () => void
  onPrintPra: () => void
  onPrintGuest: () => void
  onCreditNote: () => void
  onUndoStaffMeal: () => void
  onViewCreditNote: (id: string) => void
  printingRefund: boolean
  onPrintRefundReceipt: (id: string) => void
}) {
  // Backend ships a pre-formatted DD-MM-YYYY HH:mm `created_at_label`, but
  // the column header reads "Time" — show only the 12-hour clock so the
  // table doesn't repeat the day already in the section heading.
  const timePart = formatTimeHuman(row.created_at)
  const isSynced = row.fiscal_status === 'SYNCED'
  // F-MM-N23: never filed, so there is no IRN to reverse — a refund is a
  // plain refund, allowed on the same footing as a synced order.
  const isExempt = row.fiscal_status === 'EXEMPT'
  const isCancelledByCN = !!row.cancelled_by_credit_note
  const hasLinkedCreditNote = !!row.credit_note_id
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()
  // Non-fiscal tenants never reach fiscal_status === 'SYNCED' (there's no
  // FBR submission step), so the refund entry point falls back to the
  // order's own completion state. Backend handlers/fiscal_notes.go gates
  // the non-fiscal branch on the same `status='completed'` predicate.
  const canIssueCredit = fiscalEnabled ? isSynced || isExempt : row.status === 'completed'
  // F-MM-N30: a staff meal is never credit-noted — it is undone instead.
  const isStaffMeal = row.tax_treatment === 'staff_meal'
  const canUndoStaffMeal = isStaffMeal && row.status === 'completed' && canUndoStaffMealRole()

  return (
    <>
      <tr
        className={cn(
          'hover:bg-muted/20',
          isCancelledByCN && 'opacity-60 bg-muted/10',
        )}
      >
        <td className={cn('px-4 py-3 font-medium tabular-nums', isCancelledByCN && 'line-through')}>
          {row.order_number}
        </td>
        {showDate && (
          <td className="px-4 py-3 text-muted-foreground tabular-nums">{row.business_date_label}</td>
        )}
        <td className="px-4 py-3 text-muted-foreground tabular-nums">{timePart}</td>
        <td className="hidden md:table-cell px-4 py-3">{row.table_number ?? '—'}</td>
        <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">{row.server_name ?? '—'}</td>
        <td className="hidden lg:table-cell text-right tabular-nums px-4 py-3">{row.guest_count > 0 ? row.guest_count : '—'}</td>
        <td className={cn('text-right tabular-nums px-4 py-3 font-semibold', isCancelledByCN && 'line-through')}>
          {formatCurrency(row.total_amount)}
        </td>
        <td className="hidden md:table-cell px-4 py-3 text-muted-foreground">
          <span>{orderPaymentLabel(row)}</span>
          {row.payment_sub_method_label && (
            <span className="block text-[11px] leading-tight text-muted-foreground/80">
              {row.payment_sub_method_label}
            </span>
          )}
          {row.split_tender && (
            <Badge variant="outline" className="ml-1 text-[10px] align-middle">split</Badge>
          )}
        </td>
        <td className="hidden lg:table-cell px-4 py-3 text-muted-foreground">
          {row.order_type ? displayLabelForOrderType(row.order_type, orderTypeRows) : '—'}
        </td>
        <td className="px-4 py-3">
          <StatusBadge status={row.status} />
          {isCancelledByCN && (
            <Badge
              variant="outline"
              className="ml-1 text-[10px] gap-1 border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950 dark:text-red-200"
            >
              CN issued
            </Badge>
          )}
        </td>
        {praEnabled && (
          <td className="hidden md:table-cell px-4 py-3">
            <PraStatusBadge row={row} />
          </td>
        )}
        {fiscalEnabled && (
          <td className="hidden md:table-cell px-4 py-3">
            <FiscalStatusBadge status={row.fiscal_status} />
          </td>
        )}
        <td className="px-4 py-3 text-right">
          <div className="inline-flex flex-wrap items-center justify-end gap-2">
            <Button type="button" size="sm" variant="ghost" onClick={onView}>
              View
            </Button>
            <GuestReceiptPrintButton
              row={row}
              printing={printingGuest}
              onClick={onPrintGuest}
            />
            {praEnabled && (
              <PrintPraButton row={row} printing={printingPra} onClick={onPrintPra} />
            )}
            {canIssueCredit && !isCancelledByCN && !isStaffMeal && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 text-xs border-danger-border text-danger-fg hover:bg-danger-surface"
                onClick={onCreditNote}
              >
                <ShieldAlert className="w-3.5 h-3.5" /> Cancel / {fiscalEnabled ? 'Credit Note' : 'Refund'}
              </Button>
            )}
            {canUndoStaffMeal && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 text-xs border-danger-border text-danger-fg hover:bg-danger-surface"
                onClick={onUndoStaffMeal}
              >
                <Undo2 className="w-3.5 h-3.5" /> Undo staff meal
              </Button>
            )}
          </div>
        </td>
      </tr>
      {hasLinkedCreditNote && (
        <tr className="bg-red-50/40 dark:bg-red-950/10 border-l-4 border-l-red-500">
          <td className="px-4 py-2 text-[11px] font-mono text-red-700 dark:text-red-300">
            <span className="inline-flex items-center gap-1">
              <FileX className="w-3 h-3" /> CN → {row.credit_note_irn ?? row.credit_note_id?.slice(0, 8) ?? 'pending'}
            </span>
          </td>
          <td className="px-4 py-2 text-[11px] text-muted-foreground tabular-nums">
            {row.credit_note_issued_at_label ?? '—'}
          </td>
          <td colSpan={3} className="hidden md:table-cell px-4 py-2 text-[11px] text-red-700 dark:text-red-300 italic">
            {row.credit_note_reason ? `Reason: ${row.credit_note_reason}` : 'No reason recorded'}
          </td>
          <td className="text-right tabular-nums px-4 py-2 font-semibold text-red-700 dark:text-red-300">
            −{formatCurrency(row.credit_note_total ?? row.total_amount)}
          </td>
          <td colSpan={4} className="hidden lg:table-cell px-4 py-2 text-[11px] text-muted-foreground">
            {row.credit_note_issued_by_name ? `by ${row.credit_note_issued_by_name}` : ''}
          </td>
          <td className="px-4 py-2 text-right">
            {row.credit_note_id && (
              <div className="inline-flex items-center justify-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-[11px] h-7 text-danger-fg hover:bg-red-100 dark:hover:bg-red-900/30"
                  disabled={printingRefund}
                  onClick={() => onPrintRefundReceipt(row.credit_note_id as string)}
                >
                  {printingRefund ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3 h-3 mr-1" />
                  )}
                  Refund receipt
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-[11px] h-7 text-danger-fg hover:bg-red-100 dark:hover:bg-red-900/30"
                  onClick={() => onViewCreditNote(row.credit_note_id as string)}
                >
                  View CN
                </Button>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function StatusBadge({ status }: { status: string }) {
  let cls = 'bg-muted text-muted-foreground'
  if (status === 'completed') cls = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'
  else if (status === 'cancelled') cls = 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200'
  else if (status === 'pending' || status === 'preparing') cls = 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
  else if (status === 'out_for_delivery') cls = 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'
  return <Badge variant="outline" className={cn('text-[10px] capitalize border-transparent', cls)}>{status.replace(/_/g, ' ')}</Badge>
}

function PraStatusBadge({ row }: { row: OrdersBrowserRow }) {
  if (!row.pra_invoice_printed) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950 dark:text-amber-200">
        <XCircle className="w-3 h-3" /> Not printed
      </Badge>
    )
  }
  if (row.can_print_pra) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950 dark:text-emerald-200">
        <CheckCircle2 className="w-3 h-3" /> Printed
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
      <Clock className="w-3 h-3" /> Window expired
    </Badge>
  )
}

function FiscalStatusBadge({ status }: { status?: string | null }) {
  if (!status) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
        N/A
      </Badge>
    )
  }
  switch (status) {
    case 'SYNCED':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="w-3 h-3" /> Synced
        </Badge>
      )
    case 'PENDING':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950 dark:text-amber-200">
          <Clock className="w-3 h-3" /> Pending
        </Badge>
      )
    case 'FAILED':
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950 dark:text-red-200">
          <XCircle className="w-3 h-3" /> Failed
        </Badge>
      )
    case 'EXEMPT':
      // F-MM-N23: marketplace-settled — the platform remits the tax, so the
      // order is deliberately never sent to FBR/PRA. Neutral, not a warning.
      return (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Badge variant="outline" className="text-[10px] gap-1 border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900/40 dark:bg-indigo-950 dark:text-indigo-200">
                  Exempt
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              Settled by marketplace — not filed by design
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    default:
      return (
        <Badge variant="outline" className="text-[10px] gap-1 border-muted bg-muted text-muted-foreground">
          {status}
        </Badge>
      )
  }
}

function GuestReceiptPrintButton({
  row,
  printing,
  onClick,
}: {
  row: OrdersBrowserRow
  printing: boolean
  onClick: () => void
}) {
  const disabled = row.status !== 'completed' || printing
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className="gap-1.5 border-input text-foreground hover:bg-muted/50"
    >
      {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
      Guest receipt
    </Button>
  )
}

function PrintPraButton({
  row,
  printing,
  onClick,
}: {
  row: OrdersBrowserRow
  printing: boolean
  onClick: () => void
}) {
  const Icon = row.pra_invoice_printed ? RotateCcw : Printer
  const label = row.pra_invoice_printed ? 'Reprint PRA' : 'Print PRA'
  const button = (
    <Button
      type="button"
      size="sm"
      variant={row.can_print_pra ? 'default' : 'outline'}
      disabled={!row.can_print_pra || printing}
      onClick={onClick}
      className="gap-1.5"
    >
      {printing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </Button>
  )

  if (row.can_print_pra) return button

  const reason =
    row.can_print_pra_reason ??
    (row.tax_treatment === 'marketplace_settled'
      ? 'Settled by marketplace — not filed with PRA, so there is no tax invoice to print.'
      : 'PRA reprint not available for this order.')
  const printedNote = row.pra_invoice_printed_at_label
    ? ` Originally printed ${row.pra_invoice_printed_at_label}.`
    : ''
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs text-xs">
          {reason}{printedNote}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function OrderDrawer({
  currentOrder,
  formatCurrency,
  onClose,
  onPrintPra,
  onPrintGuest,
  printingPra,
  printingGuest,
  onCreditNote,
  onUndoStaffMeal,
  onViewCreditNote,
}: {
  currentOrder: OrdersBrowserRow | null
  formatCurrency: (n: number) => string
  onClose: () => void
  onPrintPra: () => void
  onPrintGuest: () => void
  printingPra: boolean
  printingGuest: boolean
  onCreditNote: () => void
  onUndoStaffMeal: () => void
  onViewCreditNote: (id: string) => void
}) {
  // Hold the most recently focused order so the slide-out animation has
  // content to render while Radix is still tearing the panel down. Without
  // this the drawer would flash empty for ~200ms when the user closes it.
  const [lastOrder, setLastOrder] = useState<OrdersBrowserRow | null>(currentOrder)
  useEffect(() => {
    if (currentOrder) setLastOrder(currentOrder)
  }, [currentOrder])
  const order = currentOrder ?? lastOrder
  const { enabled: fiscalEnabled } = useFiscalStatus()
  const praEnabled = usePraInvoiceEnabled()
  // Mirror the table-row gate: non-fiscal tenants fall back to order status
  // because there is no fiscal_status === 'SYNCED' transition for them.
  const canIssueCredit = order
    ? fiscalEnabled
      ? order.fiscal_status === 'SYNCED' || order.fiscal_status === 'EXEMPT'
      : order.status === 'completed'
    : false

  // Shared cache key with the printPra mutation above — opening the drawer
  // pre-warms the same cache the print path reads, so click-to-print after
  // viewing does zero extra round-trips.
  const orderDetailQuery = useQuery({
    queryKey: ['order-detail', order?.id],
    queryFn: () => apiClient.getOrder(order!.id),
    enabled: !!currentOrder && !!order?.id,
    staleTime: 60_000,
  })
  const fullOrder =
    orderDetailQuery.data?.success && orderDetailQuery.data.data
      ? orderDetailQuery.data.data
      : null
  return (
    <Sheet
      open={!!currentOrder}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent size="lg" className="p-0">
        {order && (
          <>
            <SheetHeader>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Order detail
              </p>
              <SheetTitle className="tabular-nums">{order.order_number}</SheetTitle>
              <p className="text-xs text-muted-foreground">
                Created {formatDateTimeHuman(order.created_at)}
              </p>
            </SheetHeader>
            <SheetBody className="space-y-4">
              <DetailRow label="Status">
                <StatusBadge status={order.status} />
              </DetailRow>
              <DetailRow label="Table">{order.table_number ?? '—'}</DetailRow>
              <DetailRow label="Server">{order.server_name ?? '—'}</DetailRow>
              <DetailRow label="Customer">{order.customer_name ?? '—'}</DetailRow>
              <DetailRow label="Party size">
                {order.guest_count > 0 ? order.guest_count : '—'}
              </DetailRow>
              <DetailRow label="Payment method">{orderPaymentLabel(order)}</DetailRow>
              <DetailRow label="Total">
                <span className="font-semibold">{formatCurrency(order.total_amount)}</span>
              </DetailRow>
              {order.completed_at_label && (
                <DetailRow label="Completed at">{order.completed_at_label}</DetailRow>
              )}

              <OrderReceiptCard
                creditNote={order}
                fullOrder={fullOrder}
                isLoading={orderDetailQuery.isLoading}
                isError={orderDetailQuery.isError}
                onRetry={() => orderDetailQuery.refetch()}
                formatCurrency={formatCurrency}
              />

              <div className="rounded-md border border-border bg-muted/20 p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Thermal guest receipt
                </p>
                <p className="text-[11px] text-muted-foreground">
                  Informational reprint only — does not update PRA or fiscal records.
                </p>
                <GuestReceiptPrintButton row={order} printing={printingGuest} onClick={onPrintGuest} />
              </div>

              {praEnabled && (
              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  PRA tax invoice
                </p>
                <DetailRow label="Status">
                  <PraStatusBadge row={order} />
                </DetailRow>
                {order.pra_invoice_number && (
                  <DetailRow label="Invoice #">{order.pra_invoice_number}</DetailRow>
                )}
                {order.pra_invoice_printed_at_label && (
                  <DetailRow label="First printed">{order.pra_invoice_printed_at_label}</DetailRow>
                )}
                {order.pra_invoice_reprint_count > 0 && (
                  <DetailRow label="Reprints">
                    {order.pra_invoice_reprint_count}
                    {order.pra_invoice_last_reprinted_by_name
                      ? ` (last by ${order.pra_invoice_last_reprinted_by_name})`
                      : ''}
                    {order.pra_invoice_last_reprinted_at
                      ? ` on ${formatDateTimeHuman(order.pra_invoice_last_reprinted_at)}`
                      : ''}
                  </DetailRow>
                )}
                {order.pra_late_window_expires_at && (
                  <DetailRow label="Eligible until">
                    {formatDateTimeHuman(order.pra_late_window_expires_at)}
                  </DetailRow>
                )}
                <div className="pt-3">
                  <PrintPraButton row={order} printing={printingPra} onClick={onPrintPra} />
                  {!order.can_print_pra && order.can_print_pra_reason && (
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {order.can_print_pra_reason}
                    </p>
                  )}
                </div>
              </div>
              )}

              <div className="rounded-md border p-4 space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {fiscalEnabled ? 'FBR fiscal' : 'Refund'}
                </p>
                {fiscalEnabled && (
                  <>
                    <DetailRow label="Status">
                      <FiscalStatusBadge status={order.fiscal_status} />
                    </DetailRow>
                    {order.fiscal_irn && <DetailRow label="IRN">{order.fiscal_irn}</DetailRow>}
                    {order.fiscal_authority && (
                      <DetailRow label="Authority">{order.fiscal_authority}</DetailRow>
                    )}
                  </>
                )}
                {order.tax_treatment === 'staff_meal' && order.status === 'completed' && canUndoStaffMealRole() && (
                  <div className="flex gap-2 pt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs border-danger-border text-danger-fg hover:bg-danger-surface"
                      onClick={onUndoStaffMeal}
                    >
                      <Undo2 className="w-3.5 h-3.5" /> Undo staff meal
                    </Button>
                  </div>
                )}
                {canIssueCredit && !order.cancelled_by_credit_note && order.tax_treatment !== 'staff_meal' && (
                  <div className="flex gap-2 pt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1 text-xs border-danger-border text-danger-fg hover:bg-danger-surface"
                      onClick={onCreditNote}
                    >
                      <ShieldAlert className="w-3.5 h-3.5" /> Cancel / Issue {fiscalEnabled ? 'Credit Note' : 'Refund'}
                    </Button>
                  </div>
                )}
                {order.cancelled_by_credit_note && (
                  <div className="rounded-md border border-danger-border bg-red-50/50 dark:bg-red-950/20 p-3 mt-3 space-y-1">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                      {fiscalEnabled ? 'Cancelled by Credit Note' : 'Refunded'}
                    </p>
                    {order.credit_note_irn && (
                      <DetailRow label="CN IRN">{order.credit_note_irn}</DetailRow>
                    )}
                    {order.credit_note_total != null && (
                      <DetailRow label="CN total">
                        <span className="text-red-700 dark:text-red-300">
                          −{formatCurrency(order.credit_note_total)}
                        </span>
                      </DetailRow>
                    )}
                    {order.credit_note_issued_at_label && (
                      <DetailRow label="Issued">{order.credit_note_issued_at_label}</DetailRow>
                    )}
                    {order.credit_note_issued_by_name && (
                      <DetailRow label="By">{order.credit_note_issued_by_name}</DetailRow>
                    )}
                    {order.credit_note_reason && (
                      <DetailRow label="Reason">{order.credit_note_reason}</DetailRow>
                    )}
                    {order.credit_note_id && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="mt-2 gap-1 text-xs"
                        onClick={() => onViewCreditNote(order.credit_note_id as string)}
                      >
                        View {fiscalEnabled ? 'Credit Note' : 'Refund'}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

/** Undo staff meal is a counter/manager/admin action (backend role gate). */
function canUndoStaffMealRole(): boolean {
  const role = getStoredUserRole()
  return role === 'admin' || role === 'manager' || role === 'counter'
}
